import { Chip, } from '@mui/material'
import { type ReactElement } from 'react'
import LayersIcon from '@mui/icons-material/Layers'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { COLOR_GREEN, COLOR_RED, COLOR_SKY, FS_SM, ICON_SM } from '../common/theme'

export type ChipKind =
  | 'scoretemplate'
  | 'answertweak'
  | 'fixable'
  | 'notfixable'
  | 'pass'
  | 'fail'
  | 'pending'
  | 'selected'

interface TAChipProps {
  kind: ChipKind
  label?: string
  onDelete?: () => void
  onClick?: () => void
  testId?: string
}

// Reusable Tender-Assist chip used across the scoring / improvements
// surfaces. One place to nudge the tint / sizing / icon instead of
// repeating per-kind `<Chip …/>` snippets at every call site.
//   • scoretemplate / answertweak — LayersIcon, sky / green tint.
//   • fixable / notfixable        — improvements row badges.
//   • pass / fail / pending       — checkpoint status badges (solid
//     success / error / muted background).
export default function TAChip({
  kind, label, onDelete, onClick, testId,
}: TAChipProps) {
  const tint = kind === 'scoretemplate' ? `${COLOR_SKY}50` : `${COLOR_GREEN}50`
  let icon: ReactElement | undefined = <LayersIcon sx={{ fontSize: ICON_SM }} />
  // Default body styling — white base + alpha-tinted overlay so the
  // chip reads the same on any background. Pass/fail/pending swap
  // this out for a solid status colour below.
  let bodySx: any = {
    bgcolor: 'common.white',
    backgroundImage: `linear-gradient(${tint}, ${tint})`,
  }
  let iconSx: any = { ml: '4px', mr: '-4px' }

  if (kind === 'notfixable') {
    label = label ?? 'No Fix'
    bodySx = { bgcolor: `${COLOR_RED}B0`, color: 'common.white' }
    icon = <CancelOutlinedIcon sx={{ fontSize: ICON_SM, color: 'common.white' }} />
  } else if (kind === 'fixable') {
    label = label ?? 'Fixable'
    icon = <CheckCircleOutlineIcon sx={{ fontSize: ICON_SM }} />
    bodySx = { bgcolor: `${COLOR_GREEN}B0`, color: 'common.white' }
    iconSx = { ml: '4px', mr: '-4px', color: 'common.white' }
  } else if (kind === 'pass') {
    label = label ?? 'Pass'
    icon = <CheckIcon sx={{ fontSize: ICON_SM }} />
    bodySx = { bgcolor: 'success.main', color: 'common.white' }
    iconSx = { ml: '4px', mr: '-4px', color: 'common.white' }
  } else if (kind === 'fail') {
    label = label ?? 'Fail'
    icon = <CloseIcon sx={{ fontSize: ICON_SM }} />
    bodySx = { bgcolor: 'error.main', color: 'common.white' }
    iconSx = { ml: '4px', mr: '-4px', color: 'common.white' }
  } else if (kind === 'pending') {
    label = label ?? '·'
    icon = undefined
    bodySx = { bgcolor: 'action.selected', color: 'text.disabled' }
  } else if (kind === 'selected') {
    icon = undefined
    bodySx = {
      bgcolor: 'primary.main',
      // '& .MuiChip-deleteIcon': {
      //   color: 'rgba(255,255,255,0.7)',
      //   '&:hover': { color: 'common.white' },
      // },
    }
  }

  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      onDelete={onDelete}
      onClick={onClick}
      data-testid={testId}
      sx={{
        fontSize: FS_SM,
        height: 20,
        flexShrink: 0,
        ...bodySx,
        '& .MuiChip-icon': iconSx,
      }}
    />
  )
}
